import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useTeamlessCollaborationPromptQuery } from '@trello/graphql/generated';
import { Noun, Verb, trackUe } from '@trello/analytics';
import Alerts from 'app/scripts/views/lib/alerts.coffee';
import { ErrorBoundary } from 'app/src/components/ErrorBoundary';
import { Team, Feature } from 'app/scripts/debug/constants';
import { usePopover, Popover, HideReason } from '@trello/nachos';
import { forNamespace } from 'app/common/lib/util/i18n';
import { TeamlessCollaborationCreateTeam } from './TeamlessCollaborationCreateTeam';
import styles from './TeamlessCollaborationPrompt.less';
import {
  ButtonSizes,
  ButtonTypes,
  Button,
} from 'app/common/components/controls/button/button';
const Auth = require('app/scripts/db/auth');

const format = forNamespace('teamless collaboration prompt');

interface TeamlessCollaborationPromptProps {
  boardId: string;
  onClose: () => void;
  onTeamCreated: () => void;
  target: () => HTMLButtonElement;
}

function usePrevious<T>(value: T) {
  const ref = useRef<T>();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

export const TeamlessCollaborationPrompt: React.FunctionComponent<TeamlessCollaborationPromptProps> = ({
  boardId,
  onClose,
  onTeamCreated,
  target,
}) => {
  const onShow = useCallback(() => {
    trackUe({
      category: Noun.BOARD_VIEW,
      verb: Verb.VIEWS,
      directObj: Noun.TEAMLESS_COLLABORATION_PROMPT,
      context: {
        boardId,
      },
    });
  }, [boardId]);

  const onHide = useCallback(
    (hideReason: HideReason) => {
      // We only want to permenantly dismiss the prompt if the user explicitly closes the popover
      // with the cross button
      if (hideReason === HideReason.CLICK_CLOSE_BUTTON) {
        Auth.me().dismiss(`TeamlessCollaborationPrompt-${boardId}`);
        trackUe({
          category: Noun.BOARD_VIEW,
          verb: Verb.DISMISSES,
          directObj: Noun.TEAMLESS_COLLABORATION_PROMPT,
          method: 'by clicking the close button',
          context: {
            boardId,
          },
        });
      }
      onClose();
    },
    [boardId, onClose],
  );
  const { popoverProps, show, hide } = usePopover({ onShow, onHide });
  const [hasContinuedFromPrompt, setHasContinuedFromPrompt] = useState(false);

  const { data } = useTeamlessCollaborationPromptQuery({
    variables: { boardId },
  });

  /**
   * For the prompt to be shown, the board and user must meet the following:
   * - The board does not belong to a team
   * - The board has at least 3 confirmed members
   * - The user is a board admin
   * - The user is not a member of an enterprise team
   * If the user is an enterprise team member, the Private Garden rules would
   * prevent the goal of this feature- creating a free team.
   */
  const meId = data?.member?.id;
  const meMembership = data?.board?.memberships?.find(
    (membership) => membership.idMember === meId,
  );
  const isBoardAdmin = meMembership?.memberType === 'admin';
  const isEnterpriseMember = !!data?.member?.enterprises?.length;
  const isTeamless = !data?.board?.idOrganization;
  const numberOfConfirmedCollaborators = data?.board?.members?.reduce(
    (count, member) => {
      if (member.confirmed) {
        count = count + 1;
      }
      return count;
    },
    0,
  );
  const hasEnoughCollaborators =
    numberOfConfirmedCollaborators && numberOfConfirmedCollaborators >= 3;
  const boardAndMemberMeetRequirements =
    isBoardAdmin && isTeamless && hasEnoughCollaborators && !isEnterpriseMember;

  useEffect(() => {
    if (!isTeamless) {
      hide();
    } else if (boardAndMemberMeetRequirements) {
      show();
    }
  }, [boardAndMemberMeetRequirements, hide, isTeamless, show]);

  // Show an alert if we dismiss the popover due to another member adding it to a team
  const wasTeamless = usePrevious(isTeamless);
  useEffect(() => {
    if (wasTeamless && !isTeamless && popoverProps.isVisible) {
      Alerts.show('teamless-board-added-to-team', 'info', 'board', 5000);
    }
  }, [isTeamless, popoverProps.isVisible, wasTeamless]);

  // Because we are passing in a function that returns our anchoring element from classic,
  // we want to ensure we get a 'fresh' dom reference each render (in case something causes the
  // header to rerender)
  const triggerElement = target();

  return (
    <Popover
      {...popoverProps}
      title={format('popover title')}
      triggerElement={triggerElement}
    >
      <ErrorBoundary
        tags={{
          ownershipArea: Team.BizTeam,
          feature: Feature.TeamlessCollaborationPrompt,
        }}
      >
        {hasContinuedFromPrompt ? (
          <TeamlessCollaborationCreateTeam
            boardId={boardId}
            onTeamCreated={onTeamCreated}
          />
        ) : (
          <div className={styles.createTeamPrompt}>
            <img
              src={require('resources/images/highFiveIllustration@2x.png')}
              alt=""
            />
            <h2 className={styles.createTeamTitle}>
              {format(['create team prompt', 'title'])}
            </h2>
            <p>{format(['create team prompt', 'description-2'])}</p>
            <Button
              className={styles.createTeamPromptContinueButton}
              onClick={() => setHasContinuedFromPrompt(true)}
              size={ButtonSizes.FULLWIDTH}
              buttonType={ButtonTypes.PRIMARY}
            >
              {format(['create team prompt', 'button'])}
            </Button>
          </div>
        )}
      </ErrorBoundary>
    </Popover>
  );
};
